import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import uptimeMonitorService, { UptimeMonitor, UptimeCheck, UptimeStats } from '../../services/api/uptime-monitor.service';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonitorDetailsModalProps {
  monitor: UptimeMonitor;
  onClose: () => void;
}

export default function MonitorDetailsModal({ monitor, onClose }: MonitorDetailsModalProps) {
  const { t } = useTranslation('uptime');
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<UptimeCheck[]>([]);
  const [stats, setStats] = useState<UptimeStats | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'chart'>('overview');
  const [statsDays, setStatsDays] = useState(7);

  useEffect(() => {
    loadData();
  }, [monitor.id, statsDays]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [historyRes, statsRes] = await Promise.all([
        uptimeMonitorService.getHistory(monitor.id, 100),
        uptimeMonitorService.getStats(monitor.id, statsDays),
      ]);

      if (historyRes.success) setHistory(historyRes.data);
      if (statsRes.success) setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading monitor details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up':
        return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      case 'down':
        return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const formatResponseTime = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Prepare chart data
  const chartData = {
    labels: history.slice(0, 50).reverse().map(h => {
      const date = new Date(h.checked_at);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    }),
    datasets: [
      {
        label: t('responseTime'),
        data: history.slice(0, 50).reverse().map(h => h.response_time_ms || 0),
        fill: true,
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderColor: 'rgb(16, 185, 129)',
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.parsed.y}ms`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: (value: any) => `${value}ms`,
        },
      },
    },
  };

  // Prepare status bar data (last 24 hours)
  const statusBarData = history.slice(0, 24).reverse();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="relative inline-block w-full max-w-4xl px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white dark:bg-gray-800 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:p-6">
          <div className="absolute top-4 right-4">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center space-x-4 mb-6">
            <div className={`p-3 rounded-xl ${
              monitor.status === 'up' ? 'bg-green-100 dark:bg-green-900/30' :
              monitor.status === 'down' ? 'bg-red-100 dark:bg-red-900/30' :
              'bg-gray-100 dark:bg-gray-700'
            }`}>
              {monitor.status === 'up' ? (
                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              ) : monitor.status === 'down' ? (
                <XCircleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              ) : (
                <ClockIcon className="h-8 w-8 text-gray-600 dark:text-gray-400" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{monitor.name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{monitor.url}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {t('details.overview')}
              </button>
              <button
                onClick={() => setActiveTab('chart')}
                className={`py-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'chart'
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {t('details.chart')}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'history'
                    ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                }`}
              >
                {t('details.history')}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Status Bar */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('details.last24Hours')}
                    </h3>
                    <div className="flex space-x-1">
                      {statusBarData.map((check, index) => (
                        <div
                          key={index}
                          className={`flex-1 h-8 rounded ${
                            check.status === 'up' ? 'bg-green-500' :
                            check.status === 'down' ? 'bg-red-500' :
                            'bg-yellow-500'
                          }`}
                          title={`${formatDate(check.checked_at)} - ${check.status}`}
                        />
                      ))}
                      {statusBarData.length === 0 && (
                        <div className="flex-1 h-8 rounded bg-gray-200 dark:bg-gray-700" />
                      )}
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {(stats?.uptime_percentage ?? 0).toFixed(2)}%
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('uptime')}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {formatResponseTime(stats?.avg_response_time_ms)}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('avgResponseTime')}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats?.total_checks || 0}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('totalChecks')}</div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {stats?.incidents_count || 0}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{t('incidents')}</div>
                    </div>
                  </div>

                  {/* Monitor Details */}
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                      {t('details.configuration')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('form.type')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{monitor.type.toUpperCase()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('form.method')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{monitor.method}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('form.interval')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{monitor.interval_seconds}s</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('form.timeout')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{monitor.timeout_seconds}s</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('form.expectedStatusCode')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">{monitor.expected_status_code}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('lastCheck')}:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {monitor.last_check_at ? formatDate(monitor.last_check_at) : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'chart' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <select
                      value={statsDays}
                      onChange={(e) => setStatsDays(parseInt(e.target.value))}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value={1}>{t('details.last24Hours')}</option>
                      <option value={7}>{t('details.last7Days')}</option>
                      <option value={30}>{t('details.last30Days')}</option>
                    </select>
                  </div>
                  <div className="h-80">
                    <Line data={chartData} options={chartOptions} />
                  </div>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {t('details.noHistory')}
                    </div>
                  ) : (
                    history.map((check) => (
                      <div
                        key={check.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                      >
                        <div className="flex items-center space-x-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(check.status)}`}>
                            {t(`status.${check.status}`)}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(check.checked_at)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>{formatResponseTime(check.response_time_ms)}</span>
                          {check.status_code && <span>{check.status_code}</span>}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {/* Close button */}
          <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              {t('common:buttons.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
